import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/popover';
import { MenuItem } from '@components/menu';
import { useSnackbar } from '@components/snackbar';
import { ChevronDownIcon } from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useFileAccess } from 'src/features/files/hooks/useFileAccess';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';
import { StatusColors } from 'src/features/scrum/components/table/CellRenderers';
import { useAgileStore } from 'src/features/scrum/store';

import { useTasksStore } from '../store/useTasksStore';

const { useCallback, useMemo, useEffect } = React;

export const EditItemStatusColumn = () => {
  const taskId = useTasksStore((s) => s.currentTaskId);
  const item = useTasksStore(
    useCallback(
      (s) => {
        if (taskId) {
          return s.tasks[taskId];
        }
      },
      [taskId]
    )
  );

  const [open] = useSnackbar();

  const router = useRouter();
  const isAgile = router?.query?.type === 'agile';

  const { isReadOnly } = useTaskAccess(taskId || '');

  const [showMenu, setShowMenu] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const changeSprintItemStatus = useAgileStore((s) => s.changeSprintItemStatus);
  const onUpdateColumn = useTasksStore((s) => s.onUpdateTaskColumn);
  const setTask = useTasksStore((s) => s.setTask);
  const [columnOptions, setColumnOptions] = React.useState<any[]>([]);

  useEffect(() => {
    if (item?.board?.columns) {
      setColumnOptions(item?.board?.columns);
    }
  }, [item?.board?.columns]);

  const selectedColumn = columnOptions?.find((col) => col._id === item?.column);
  const selectedColumnColor =
    (selectedColumn &&
      (isAgile
        ? StatusColors[selectedColumn.type] || selectedColumn.color
        : selectedColumn.color)) ||
    '#FD9740';

  const handleColChange = async (optionId: string) => {
    if (item?.column === optionId) return setShowMenu(false);
    if (isAgile) {
      if (!item?.board?.id) return;
      setTask(item.id || '', { ...item, column: optionId });
      changeSprintItemStatus(
        item?.id || '',
        '',
        item?.column || '',
        optionId,
        item?.board?.id || ''
      );
    } else {
      setLoading(true);
      onUpdateColumn({ taskId: item?.id || '', columnId: optionId })
        .then((success) => {
          if (!success)
            return open({
              text: 'Something went wrong',
              duration: 2000,
              position: 'top-center',
              snackType: 'error',
            });
        })
        .finally(() => setLoading(false));
    }
    setShowMenu(false);
  };

  return (
    <div>
      <Popover
        placement="bottom-start"
        isOpen={!isReadOnly && showMenu}
        onClose={() => setShowMenu(false)}
      >
        <PopoverTrigger>
          <Tippy
            disabled={!isReadOnly}
            content={'You do not have the required permission to edit tasks'}
          >
            <button
              className={classNames(
                'px-12 py-6 rounded text-gray-700 flex items-center gap-6',
                loading && 'opacity-50'
              )}
              style={{ backgroundColor: selectedColumnColor }}
              onClick={() => !loading && setShowMenu(!showMenu)}
            >
              <span className="flex-grow text-sm">{selectedColumn?.name || 'Select Status'}</span>
              {!isReadOnly && <ChevronDownIcon />}
            </button>
          </Tippy>
        </PopoverTrigger>
        <PopoverContent>
          <div className="bg-white rounded-lg border p-6 min-w-200 overflow-hidden">
            {columnOptions.map((option) => {
              const agileColor = StatusColors[option.type] || option.color;
              const statusColor = isAgile ? agileColor : option.color;
              return (
                <MenuItem
                  key={option._id}
                  className="flex items-center hover:bg-gray-50 gap-8 px-12 py-6"
                  onClick={handleColChange.bind(null, option._id)}
                >
                  <div className="w-14 h-14 " style={{ backgroundColor: statusColor }} />
                  {option.name}
                </MenuItem>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
